const QUEUE_SIZE = 15 // 队列默认大小
const EXPEND_SIZE = 10 // 扩容时每次扩多大
const REDUCE_SIZE = 5 // 缩容时，每次缩多大
const MAX_QUEUE_SIZE = 100 // 最多扩到这么大
// 循环队列（可根据情况自动扩容、自动缩容）
// useage: const queue = new Queue(); queue.push(1); queue.pop();
export class Queue {
  constructor (options = {}) {
    const { size = QUEUE_SIZE } = options
    this.queue = new Array(size)
    this.head = this.tail = 0
    this.size = size
    this.currentSize = 0
  }
  push (item) {
    if (this.currentSize === this.size && this.currentSize < MAX_QUEUE_SIZE) {
      // 扩容
      this.expend()
    }
    this.queue[this.tail++] = item
    this.tail = this.tail % this.size
    this.currentSize++
  }
  pop () {
    if (this.currentSize === 0) {
      // 缩容
      this.reduce()
      return
    }
    const item = this.queue[this.head]
    this.queue[this.head++] = undefined
    this.head = this.head % this.size
    this.currentSize--
    return item
  }
  expend (item) {
    this.size += EXPEND_SIZE
    const originalQueue = this.queue
    const newQueue = this.queue = new Array(this.size)
    const orgLen = originalQueue.length
    const tail = this.tail
    const head = this.head
    if (tail === head && this.currentSize > 0) {
      let j = 0
      for (let i = head; i < orgLen; i++) {
        newQueue[j++] = originalQueue[i]
      }
      for (let i = 0; i < tail; i++) {
        newQueue[j++] = originalQueue[i]
      }
      this.head = 0
      this.tail = orgLen
    }
  }
  reduce () {
    if (this.size - REDUCE_SIZE < QUEUE_SIZE) {
      return
    }
    this.size = this.size - REDUCE_SIZE
    this.queue = new Array(this.size)
    this.reset()
  }
  reset () {
    this.head = this.tail = 0
  }
}

/**
  轮询类（维护三个队列： task 队列，result 队列，fail 队列）：轮询接口，并将轮询结果通过 callback 传给外部
    ① 定时从 task 队列拿出 task，发送请求
      1、请求成功后将结果推入 result 队列，终止任务
      2、请求失败，回到 task 队列，排队等待再次请求
    ② 定时从 result 队列取结果，调用 callback
    ③ task 队列有任务时，自动启动轮询，无任务时自动停止轮询
    ④ result 队列有结果时，自动启用轮询，无结果时自动停止
    ⑤ 实时将列队更新同步到 localstorage
  useage: 使用时，一个接口一个实例
    const polling = new Polling({
      taskName: string,
      advertiserID: string,
      callback: function xxx(task:object ,result: object): void,
      startPollingInterval?: number,
      resultPollingInterval?: number,
      limitReqCount?: number,
      size?: number,
      requestDefault: {
        method: string,
        url: string,
        data: object
      },
      storage?: {
        use: boolean,
        expire: number
      },
      startImmediate?: boolean,
      goBackPendingQueue?: function xxx(task:object ,result: object): boolean
    })
    polling.pendingQueuePush({
      url: string, // 可选，覆盖 requestDefault.url
      method: string, // 可选，覆盖 requestDefault.method
      data: object // 可选，覆盖 requestDefault.data
    })
    // 自动开始轮询
    polling.pendingQueuePush(task)
  attrs:
    taskName: 任务名，默认开启 localstorage, 必填。关闭 localstoreage 可不传
    advertiserID: 账户 id，由于平台会有多商户，所以将账户 ID 作为必传参数
    callback: 可选，轮询结果回调函数，将轮询结果跟之前对应的任务传到 callback 中
    startPollingInterval: 可选，请求队列的轮询间隔时间（毫秒），默认 4500
    resultPollingInterval: 可选，结果队列的轮询时间（毫秒），默认 4500
    limitReqCount: 可选，每个 task 最多执行多少次，默认 4 次
    size: 可选，轮询任务队列大小，默认 15，容量不足，每次扩容加 10 ，为空时，自动缩容减 5
    requestDefault: 可选，每次发送请求时对应的信息。作为默认的发送请求时的信息，如果不指定需要在 task 中指定。task 中指定的优先级更高
    storage:
      use: 可选，是否使用 locastoreage，默认开启，开启后下次进入页面会使用之前的队列,
      expire: 可选，每个任务的过期时间，过期之后不再轮询，默认为 1 天，单位为 ms
    startImmediate: 可选，在实例化时是否立即启动轮询，开启后第一次实例化时，如果有历史数据，会立即开始轮询
    goBackPendingQueue: 可选，决定每一个轮询成功后的 task，是否继续轮询
  Apis: 使用时，关心一下 Api 即可
    pendingQueuePush: 向 task 队列增加一个轮询任务
  note:
    1、requestDefault 和 task 组合起来一定要有 method、url、data
    2、task 队列、result 队列有任务时会自动开始轮询，无任务时自动停止轮询
    3、大部分都有默认值，可根据需要自定义
 */
const START_POLLING_INTERVAL = 4500 // task 队列轮询间隔默认时间
const RESULT_POLLING_INTERVAL = 4500 // result 队列轮询间隔默认时间
const LIMIT_REQ_COUNT = 4 // 默认上限请求次数
const DEFAULT_METHOD = 'get' // 默认请求方法
const EXPIRE_TIME = 1 * 24 * 60 * 1000 // 默认任务过期时间
const MAX_ERROR_COUNT = 12 // 默认接口出错多少次之后，该轮询任务会被抛弃

export class Polling {
  timerForPendingQueue = null
  timerForResultQueue = null
  constructor (options = {}) {
    let {
      taskName,
      callback,
      advertiserID,
      startPollingInterval = START_POLLING_INTERVAL,
      resultPollingInterval = RESULT_POLLING_INTERVAL,
      limitReqCount = LIMIT_REQ_COUNT,
      size,
      requestDefault,
      storage,
      startImmediate = true,
      goBackPendingQueue
    } = options
    storage = Object.assign({
      use: false,
      expire: EXPIRE_TIME
    }, storage)
    if (storage.use && (!taskName || !advertiserID)) {
      console.error(`开启 localStorage 需要一个唯一的 taskName 和 advertiserID，请传入 taskName 或者 advertiserID`)
      return
    }
    window.pollingTaskList = window.pollingTaskList || []
    if (window.pollingTaskList.includes(taskName)) {
      console.error(`名为'${taskName}'的任务已存在`)
      return
    }
    window.pollingTaskList = window.pollingTaskList.push(taskName)
    this.storageKey = `${taskName}-${advertiserID}`
    this.startPollingInterval = startPollingInterval
    this.resultPollingInterval = resultPollingInterval
    this.callback = callback
    this.limitReqCount = limitReqCount
    this.requestDefault = requestDefault
    this.storage = storage
    this.startImmediate = startImmediate
    this.goBackPendingQueue = goBackPendingQueue
    this.size = size
    this.rebuildQueue()
    this.checkHistory()
  }
  /**
   * 1、实例化 queue
   * 2、如果 locastoreage 中有值，将其取出，由于存到 localstore 中的 queue 变为了一个字符串，需要重新将其包含的信息，更新到 queue 中
   */
  rebuildQueue () {
    const storeString = window.localStorage.getItem(this.storageKey)
    const size = this.size
    const pendingQueue = this.pendingQueue = new Queue(size)
    const resultQueue = this.resultQueue = new Queue(size)
    const failQueue = this.failQueue = new Queue(size)
    if (!storeString) {
      return
    }
    let store
    try {
      store = JSON.parse(storeString)
    } catch (error) {
      store = {}
    }
    let {
      pendingQueue: oldPendingQueue,
      resultQueue: oldResultQueue,
      failQueue: oldFailQueue
    } = store
    if (oldPendingQueue && oldPendingQueue.currentSize > 0) {
      this.mergeQueue(oldPendingQueue, pendingQueue)
    }
    if (oldResultQueue && oldResultQueue.currentSize > 0) {
      this.mergeQueue(oldResultQueue, resultQueue)
    }
    if (oldFailQueue && oldFailQueue.currentSize > 0) {
      this.mergeQueue(oldFailQueue, failQueue)
      this.copyQueue(failQueue, pendingQueue)
    }
    oldPendingQueue = null
    oldResultQueue = null
    oldFailQueue = null
  }
  mergeQueue (currentQueue, targetQueue) {
    targetQueue.queue = JSON.parse(JSON.stringify(currentQueue.queue))
    targetQueue.tail = currentQueue.tail
    targetQueue.head = currentQueue.head
    targetQueue.currentSize = currentQueue.currentSize
  }
  copyQueue (currentQueue, targetQueue) {
    let task = currentQueue.pop()
    while (task) {
      targetQueue.push(task)
      task = currentQueue.pop()
    }
  }
  // 检查之前 pendingQueue、resultQueue内是否还有任务，如果有，那么自动启动轮询
  checkHistory () {
    if (!this.startImmediate) {
      return
    }
    if (this.pendingQueue.currentSize > 0) {
      this.startPolling()
    }
    if (this.resultQueue.currentSize > 0) {
      this.startResultPolling()
    }
  }
  pendingQueuePop () {
    const task = this.pendingQueue.pop()
    this.update2LocalStorage()
    if (!task) {
      console.info('没有任务啦！')
      this.stopPolling()
      return
    }
    // 过期，时间超过一天
    if (
      this.storage.use &&
      Date.now() - this.storage.expire > task.createTime
    ) {
      console.info('task 过期了')
      // 取下一个
      return this.pendingQueuePop()
    }
    if (task.errorCount > MAX_ERROR_COUNT) {
      console.warn(`task 错误次数超过${MAX_ERROR_COUNT}，该 task 不再轮询`)
      return this.pendingQueuePop()
    }
    task.reqCount += 1
    // 达到执行次数上限后进入 fail 队列
    if (task.reqCount > this.limitReqCount) {
      console.info('task 达到执行次数上限')
      this.failQueue.push({
        ...task,
        reqCount: 0 // failQueue 为失败队列，下次进入页面会将其拿出来重新请求，需要将次数重置为 0
      })
      this.update2LocalStorage()
      // 取下一个
      return this.pendingQueuePop()
    }
    return task
  }
  pendingQueuePush (task) {
    if (!task) {
      return
    }
    // 之前的旧任务不需要再添加这些数据
    if (task.reqCount === undefined) {
      task.reqCount = 0
    }
    if (task.createTime === undefined && this.storage.use) {
      task.createTime = Date.now()
    }
    if (task.errorCount === undefined) {
      task.errorCount = 0
    }
    this.pendingQueue.push(task)
    this.update2LocalStorage()
    // 当之前队列为空时，轮询任务被中断，需要重新开启
    if (!this.timerForPendingQueue) {
      this.startPolling()
    }
  }
  startPolling () {
    if (this.timerForPendingQueue) {
      return
    }
    this.timerForPendingQueue = setInterval(() => {
      const task = this.pendingQueuePop()
      // 没有任务时不发送请求
      if (task) {
        this.request(task)
      }
    }, this.startPollingInterval)
  }
  stopPolling () {
    clearInterval(this.timerForPendingQueue)
    this.timerForPendingQueue = null
  }
  request (task) {
    const { data, url, method = DEFAULT_METHOD } = Object.assign({}, this.requestDefault, task)
    if (!url) {
      throw new Error('url 不能为空，请在requestDefault或者task中传入')
    }
    const func = method === 'get' ? BUI.fetch : BUI.update
    func({
      url,
      contentType: 'application/json',
      data,
      // 请求成功后，如果没有提供 goBackPendingQueue，默认将其放入结果队列
      success: (resp) => {
        if (resp.code !== 0) {
          task.errorCount += 1
          this.pendingQueuePush(task)
          return
        }
        if (
          this.goBackPendingQueue &&
          typeof this.goBackPendingQueue === 'function'
        ) {
          const flag = this.goBackPendingQueue(task, resp)
          if (flag) {
            this.pendingQueuePush(task)
            return
          }
        }
        this.resultQueuePush({
          task,
          resp
        })
      },
      error: () => {
        task.errorCount += 1
        this.pendingQueuePush(task)
      }
    })
  }
  resultQueuePush (task) {
    if (!task) {
      return
    }
    this.resultQueue.push(task)
    this.update2LocalStorage()
    if (!this.timerForResultQueue) {
      this.startResultPolling()
    }
  }
  resultQueuePop () {
    const task = this.resultQueue.pop()
    this.update2LocalStorage()
    if (!task) {
      this.stopResultPolling()
      return
    }
    return task
  }
  startResultPolling () {
    if (this.timerForResultQueue) {
      return
    }
    this.timerForResultQueue = setInterval(() => {
      const { task, resp } = this.resultQueuePop() || {}
      if (task && this.callback) {
        this.callback(task, resp)
      }
    }, this.startPollingInterval)
  }
  stopResultPolling () {
    clearInterval(this.timerForResultQueue)
    this.timerForResultQueue = null
  }
  update2LocalStorage () {
    if (!this.storage.use) {
      return
    }
    window.localStorage.setItem(this.storageKey, JSON.stringify({
      pendingQueue: this.pendingQueue,
      resultQueue: this.resultQueue,
      failQueue: this.failQueue
    }))
  }
}
